import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Menu, X } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';

const NAV_HREFS = [
  { key: 'pricing', href: '#pricing' },
  { key: 'services', href: '#services' },
  { key: 'process', href: '#process' },
  { key: 'sold', href: '#sold' },
  { key: 'contact', href: '#contact' },
];

const Navbar = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const anchorHref = (href) => {
    if (!href || !href.startsWith('#')) return href;
    return onHome ? href : `/${href}`;
  };

  const handleLogoClick = (e) => {
    if (onHome) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setOpen(false);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'backdrop-blur-md bg-[#07120F]/70 border-b border-white/5' : 'bg-transparent'}`}>
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
        <Link to="/" onClick={handleLogoClick} className="flex items-center gap-2 group" aria-label={t('nav.home')} data-testid="navbar-logo">
          <span className="w-7 h-7 rounded-full bg-[#5FE0C8] grid place-items-center">
            <span className="w-2 h-2 rounded-full bg-[#07120F]" />
          </span>
          <span className="font-display text-[17px] tracking-tight">Mint <span className="text-[#7A7A7A]">Property Advisory</span></span>
        </Link>
        <nav className="hidden md:flex items-center gap-7 lg:gap-8">
          {NAV_HREFS.map(l => (
            <a
              key={l.key}
              href={anchorHref(l.href)}
              data-testid={`navlink-${l.key}`}
              className="text-[13.5px] text-[#C9C5B8] hover:text-[#5FE0C8] transition-colors"
            >
              {t(`nav.links.${l.key}`)}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2 md:gap-3">
          <div className="hidden md:block">
            <LanguageSwitcher variant="desktop" />
          </div>
          <a href={anchorHref('#contact')} data-testid="navbar-book-consult" className="hidden md:inline-flex items-center gap-1.5 btn-mint px-4 py-2.5 rounded-full text-[13px]">
            {t('nav.bookConsult')} <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
          </a>
          <div className="md:hidden">
            <LanguageSwitcher variant="mobile" compact />
          </div>
          <button onClick={() => setOpen(!open)} data-testid="navbar-menu-toggle" className="md:hidden w-10 h-10 grid place-items-center rounded-full border border-white/10" aria-label={open ? 'Close menu' : 'Open menu'}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/5 bg-[#07120F]" data-testid="navbar-mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_HREFS.map(l => (
              <a key={l.key} href={anchorHref(l.href)} onClick={() => setOpen(false)} data-testid={`mobile-navlink-${l.key}`} className="text-[15px] text-[#C9C5B8]">{t(`nav.links.${l.key}`)}</a>
            ))}
            <a href={anchorHref('#contact')} onClick={() => setOpen(false)} data-testid="mobile-book-consult" className="btn-mint inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-full text-[14px]">
              {t('nav.bookConsult')} <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
